/**
 * What the TV scorebug shows, read off the game log: every event that carries the situation, a
 * score, or a new possession updates it, and the pending call's game sets it exactly between plays.
 * The words come from the sim (periodName, downName, sideSpotName); nothing is worked out here.
 */
import {
  balance,
  downName,
  periodName,
  sideSpotName,
  type Game,
  type GameEvent,
  type Team,
  type TeamId,
} from '@tdmons/sim';
import { teamTag } from './steps';

export interface Scoreboard {
  quarter: number;
  clock: number;
  score: Record<TeamId, number>;
  /** Who has the ball (or receives the kickoff). */
  possession: TeamId;
  ballOn: number;
  /** Down and distance; null while it is not a down (a kickoff, before the first snap). */
  down: { down: number; toGo: number } | null;
  /** The try after a touchdown. */
  conversion: boolean;
}

export function scoreboardOf(game: Readonly<Game>): Scoreboard {
  return {
    quarter: game.quarter,
    clock: game.clock,
    score: { ...game.score },
    possession: game.possession,
    ballOn: game.ballOn,
    down: game.phase === 'scrimmage' ? { down: game.down, toGo: game.toGo } : null,
    conversion: game.phase === 'conversion',
  };
}

/** The scorebug after `e`. */
export function scoreboardAfter(s: Scoreboard, e: GameEvent): Scoreboard {
  switch (e.type) {
    case 'snap':
    case 'punt':
    case 'field-goal':
    case 'extra-point': {
      const conversion = e.type === 'extra-point' || (e.type === 'snap' && e.kind === 'two-point');
      return {
        quarter: e.at.quarter,
        clock: e.at.clock,
        score: { ...e.at.score },
        possession: e.at.offense,
        ballOn: e.at.ballOn,
        down: conversion ? null : { down: e.at.down, toGo: e.at.toGo },
        conversion,
      };
    }
    case 'score':
    case 'final':
    case 'halftime':
    case 'quarter-end':
      return { ...s, score: { ...e.score } };
    case 'kickoff':
      return { ...s, possession: e.receiving, ballOn: e.ballOn, down: null, conversion: false };
    case 'possession':
      return { ...s, possession: e.team, ballOn: e.ballOn, down: null, conversion: false };
    case 'overtime':
      return { ...s, quarter: balance.football.quarters + e.period, clock: 0 };
    default:
      return s;
  }
}

/** The scorebug before the opening kickoff. */
export function openingScoreboard(): Scoreboard {
  return {
    quarter: 1,
    clock: balance.football.quarterSeconds,
    score: { A: 0, B: 0 },
    possession: 'A',
    ballOn: balance.football.kickoffStartYardLine,
    down: null,
    conversion: false,
  };
}

/** The scorebug's text: team tags, the period and clock, "2ND & 3", "OWN 32". */
export interface ScorebugText {
  tags: Record<TeamId, string>;
  score: Record<TeamId, string>;
  possession: TeamId;
  period: string;
  down: string;
  spot: string;
}

export function scorebugText(s: Scoreboard, teams: Record<TeamId, Team>): ScorebugText {
  const tags = { A: teamTag(teams.A), B: teamTag(teams.B) };
  const defense: TeamId = s.possession === 'A' ? 'B' : 'A';
  return {
    tags,
    score: { A: String(s.score.A), B: String(s.score.B) },
    possession: s.possession,
    period: periodName(s.quarter, s.clock, balance.football.quarters).toUpperCase(),
    down: s.conversion
      ? 'TRY'
      : s.down
        ? downName(s.down.down, s.down.toGo, s.ballOn).toUpperCase()
        : '',
    // The try's spot depends on the kick or the two-point snap, chosen after this.
    spot: s.conversion ? '' : sideSpotName(s.ballOn, tags[defense]).toUpperCase(),
  };
}
